import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Trophy,
  Search,
  Filter,
  Download,
  Medal,
  User,
  Sparkles,
  Swords,
  Gamepad2,
  History,
  ArrowLeft,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  listEvents,
  listEventStandings,
  listUsers,
  listEventPlayerResults,
  listPlayerConstructs,
  listEventTopCutResults,
  listLeaguePointAdjustments,
  listLeagues,
} from "../graphql/queries";
import { saveLeagueStandings, CalculatedStanding } from "../lib/api/standings";
import LeagueSelector from "../components/LeagueSelector";
import {
  getLeagueGame,
  getLeagueGameLabel,
  LEAGUE_GAMES,
  LeagueGame,
} from "../lib/leagueGames";
import { getCurrentLeague, getLeagueStatus, League } from "../lib/leagueUtils";
import {
  applyPremiumMultiplier,
  calculateParticipationPoints,
  calculateRecordPoints,
  calculateTop8Points,
  getLeagueScoring,
} from "../lib/scoring";
import {
  calculateFinishPercentile,
  calculateWinRate,
  compareLeagueStandings,
} from "../lib/playerStats";
import { getCompetitionRanks } from "../lib/commander";

const client = generateClient();

interface StandingsProps {
  view?: "active" | "past";
}

const Standings: React.FC<StandingsProps> = ({ view = "active" }) => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const isPastView = view === "past";
  const [searchTerm, setSearchTerm] = useState("");
  const [minEvents, setMinEvents] = useState<string>("0");

  // Fetch all leagues for the selector (shared with LeagueSelector component)
  const { data: leagues } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });

  const selectedLeague = leagues?.find((league: any) => league.id === leagueId);
  const selectedGame = getLeagueGame(selectedLeague?.game);
  const selectedLeagueStatus = selectedLeague
    ? getLeagueStatus(selectedLeague as League)
    : null;
  const scoring = useMemo(
    () => getLeagueScoring(selectedLeague),
    [selectedLeague]
  );

  const displayedLeagueByGame = useMemo(() => {
    const allLeagues = (leagues || []) as League[];

    return (Object.keys(LEAGUE_GAMES) as LeagueGame[]).reduce(
      (result, game) => {
        const gameLeagues = allLeagues.filter(
          (league) => getLeagueGame(league.game) === game
        );

        if (isPastView) {
          const pastLeagues = gameLeagues
            .filter((league) =>
              ["ended", "inactive"].includes(getLeagueStatus(league).status)
            )
            .sort((a, b) => {
              const aDate = a.endDate || a.startDate;
              const bDate = b.endDate || b.startDate;
              return bDate.localeCompare(aDate);
            });
          result[game] = pastLeagues[0] || null;
        } else {
          result[game] =
            getCurrentLeague(
              gameLeagues.filter(
                (league) => getLeagueStatus(league).status === "running"
              )
            ) || null;
        }

        return result;
      },
      {} as Record<LeagueGame, League | null>
    );
  }, [isPastView, leagues]);

  const alternateLeagueByGame = useMemo(() => {
    const allLeagues = (leagues || []) as League[];

    return (Object.keys(LEAGUE_GAMES) as LeagueGame[]).reduce(
      (result, game) => {
        const gameLeagues = allLeagues.filter(
          (league) => getLeagueGame(league.game) === game
        );
        const runningLeague =
          getCurrentLeague(
            gameLeagues.filter(
              (league) => getLeagueStatus(league).status === "running"
            )
          ) || null;
        const pastLeague =
          gameLeagues
            .filter((league) =>
              ["ended", "inactive"].includes(getLeagueStatus(league).status)
            )
            .sort((a, b) =>
              (b.endDate || b.startDate).localeCompare(a.endDate || a.startDate)
            )[0] || null;

        result[game] = isPastView ? runningLeague : pastLeague;
        return result;
      },
      {} as Record<LeagueGame, League | null>
    );
  }, [isPastView, leagues]);

  React.useEffect(() => {
    if (!leagues) return;

    const selectedStatus = selectedLeague
      ? getLeagueStatus(selectedLeague as League).status
      : null;
    const selectedIsValid = isPastView
      ? selectedStatus === "ended" || selectedStatus === "inactive"
      : selectedStatus === "running";
    const fallbackLeague = displayedLeagueByGame[selectedGame];

    if (!selectedIsValid && fallbackLeague && fallbackLeague.id !== leagueId) {
      navigate(
        `/league/${fallbackLeague.id}/standings${isPastView ? "/history" : ""}`,
        { replace: true }
      );
    }
  }, [
    displayedLeagueByGame,
    isPastView,
    leagueId,
    leagues,
    navigate,
    selectedGame,
    selectedLeague,
  ]);

  // Handle league change by navigating to the new league's standings
  const handleLeagueChange = (newLeagueId: string | null) => {
    if (newLeagueId) {
      navigate(
        `/league/${newLeagueId}/standings${isPastView ? "/history" : ""}`
      );
    }
  };

  // Fetch events for this league
  const {
    data: events,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["leagueEvents", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: {
          filter: { leagueId: { eq: leagueId } },
          limit: 100,
        },
      });
      return result.data.listEvents.items;
    },
    enabled: !!leagueId,
  });

  // Fetch all event standings
  const {
    data: allStandings,
    isLoading: standingsLoading,
    error: standingsError,
  } = useQuery({
    queryKey: ["allEventStandings"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventStandings,
        variables: { limit: 1000 },
      });
      return result.data.listEventStandings.items;
    },
  });

  // Fetch all event player results (from EventLink imports)
  const {
    data: allEventPlayerResults,
    isLoading: eventPlayerResultsLoading,
    error: eventPlayerResultsError,
  } = useQuery({
    queryKey: ["allEventPlayerResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventPlayerResults.items;
    },
  });

  // Fetch all player constructs
  const {
    data: allPlayerConstructs,
    isLoading: playerConstructsLoading,
    error: playerConstructsError,
  } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  // Fetch all users
  const {
    data: allUsers,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["allUsers"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return result.data.listUsers.items;
    },
  });

  // Fetch all top cut results
  const {
    data: allTopCutResults,
    isLoading: topCutResultsLoading,
    error: topCutResultsError,
  } = useQuery({
    queryKey: ["allTopCutResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventTopCutResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventTopCutResults.items;
    },
  });

  const {
    data: leaguePointAdjustments,
    isLoading: adjustmentsLoading,
    error: adjustmentsError,
  } = useQuery({
    queryKey: ["leaguePointAdjustments", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeaguePointAdjustments,
        variables: {
          filter: { leagueId: { eq: leagueId } },
          limit: 1000,
        },
      });
      return result.data.listLeaguePointAdjustments.items;
    },
    enabled: !!leagueId,
  });

  // Calculate league standings
  const leagueStandings = useMemo(() => {
    if (
      !events ||
      !allStandings ||
      !allUsers ||
      !allEventPlayerResults ||
      !allPlayerConstructs ||
      !allTopCutResults ||
      !leaguePointAdjustments
    )
      return [];

    // Get event IDs for this league
    const leagueEventIds = events.map((event: any) => event.id);
    const getEventFieldSize = (eventId: string) => {
      const event: any = events.find((item: any) => item.id === eventId);
      if (event?.fieldSize) return event.fieldSize;
      return Math.max(
        allEventPlayerResults.filter(
          (result: any) => result.eventId === eventId
        ).length,
        allStandings.filter((standing: any) => standing.eventId === eventId)
          .length,
        1
      );
    };

    // Filter standings for this league's events
    const leagueStandingsData = allStandings.filter((standing: any) =>
      leagueEventIds.includes(standing.eventId)
    );

    // Filter event player results for this league's events
    const leagueEventPlayerResults = allEventPlayerResults.filter(
      (result: any) => leagueEventIds.includes(result.eventId)
    );

    // Group standings by user and calculate totals
    const userStandings: Record<string, any> = {};

    // Process traditional EventStanding records
    leagueStandingsData.forEach((standing: any) => {
      if (!standing.userId) return; // Skip unassigned standings

      const user = allUsers.find((u: any) => u.id === standing.userId);
      if (!user) return;

      if (!userStandings[standing.userId]) {
        userStandings[standing.userId] = {
          userId: standing.userId,
          user: user,
          totalPoints: 0,
          totalEvents: 0,
          bestRank: Infinity,
          averageRank: 0,
          totalRanks: 0,
          totalFinishPercentile: 0,
          totalWins: 0,
          totalLosses: 0,
          totalDraws: 0,
          events: [],
        };
      }

      const userStanding = userStandings[standing.userId];
      const event = events.find((e: any) => e.id === standing.eventId);
      const isPremium = event?.isPremium === true; // Explicitly check for true
      const pointsToAdd = applyPremiumMultiplier(
        standing.points,
        isPremium,
        scoring
      );

      userStanding.totalPoints += pointsToAdd;
      userStanding.totalEvents += 1;
      userStanding.bestRank = Math.min(userStanding.bestRank, standing.rank);
      userStanding.totalRanks += standing.rank;
      userStanding.totalFinishPercentile += calculateFinishPercentile(
        standing.rank,
        getEventFieldSize(standing.eventId)
      );
      userStanding.totalWins += standing.wins || 0;
      userStanding.totalLosses += standing.losses || 0;
      userStanding.totalDraws += standing.draws || 0;
      userStanding.events.push({
        eventId: standing.eventId,
        rank: standing.rank,
        points: standing.points,
        pointsAwarded: pointsToAdd, // Store the actual points awarded (with multiplier)
        omw: standing.omw,
        gw: standing.gw,
        ogw: standing.ogw,
        isPremium: isPremium,
      });
    });

    // Process EventPlayerResult records (from EventLink imports)
    leagueEventPlayerResults.forEach((result: any) => {
      const playerConstruct = allPlayerConstructs.find(
        (pc: any) => pc.id === result.playerConstructId
      );
      if (!playerConstruct) return;

      const constructKey = `construct_${playerConstruct.id}`;
      const standingKey = playerConstruct.userId || constructKey;

      if (!userStandings[standingKey]) {
        const linkedUser = playerConstruct.userId
          ? allUsers.find((u: any) => u.id === playerConstruct.userId)
          : null;

        userStandings[standingKey] = {
          userId: playerConstruct.userId || null,
          user: linkedUser || null,
          playerConstruct: playerConstruct,
          totalPoints: 0,
          totalEvents: 0,
          bestRank: Infinity,
          averageRank: 0,
          totalRanks: 0,
          totalFinishPercentile: 0,
          totalWins: 0,
          totalLosses: 0,
          totalDraws: 0,
          events: [],
        };
      } else if (!userStandings[standingKey].playerConstruct) {
        userStandings[standingKey].playerConstruct = playerConstruct;
      }

      const userStanding = userStandings[standingKey];
      const event = events.find((e: any) => e.id === result.eventId);
      const isPremium = event?.isPremium === true; // Explicitly check for true
      const pointsToAdd = applyPremiumMultiplier(
        event?.format === "Commander"
          ? result.points || 0
          : calculateRecordPoints(
              result.wins || 0,
              result.draws || 0,
              result.losses || 0,
              scoring
            ),
        isPremium,
        scoring
      );

      userStanding.totalPoints += pointsToAdd;
      userStanding.totalEvents += 1;
      userStanding.bestRank = Math.min(userStanding.bestRank, result.rank);
      userStanding.totalRanks += result.rank;
      userStanding.totalFinishPercentile += calculateFinishPercentile(
        result.rank,
        getEventFieldSize(result.eventId)
      );
      userStanding.totalWins += result.wins || 0;
      userStanding.totalLosses += result.losses || 0;
      userStanding.totalDraws += result.draws || 0;
      userStanding.events.push({
        eventId: result.eventId,
        rank: result.rank,
        points: result.points,
        pointsAwarded: pointsToAdd, // Store the actual points awarded (with multiplier)
        omw: result.omw,
        gw: result.gw,
        ogw: result.ogw,
        isPremium: isPremium,
      });
    });

    // Process top cut results for this league
    const leagueTopCutResults = allTopCutResults.filter(
      (result: any) =>
        leagueEventIds.includes(result.eventId) &&
        events.find((event: any) => event.id === result.eventId)?.format !==
          "Commander"
    );

    leagueTopCutResults.forEach((result: any) => {
      let userStanding;

      if (result.userId) {
        // Top cut result for a user
        userStanding = userStandings[result.userId];
      } else if (result.playerConstructId) {
        const playerConstruct = allPlayerConstructs.find(
          (construct: any) => construct.id === result.playerConstructId
        );
        const standingKey =
          playerConstruct?.userId ||
          `construct_${result.playerConstructId}`;
        userStanding = userStandings[standingKey];
      }

      if (userStanding) {
        const event = events.find((e: any) => e.id === result.eventId);
        const isPremium = event?.isPremium === true;
        const bonusPointsToAdd = applyPremiumMultiplier(
          calculateTop8Points(result.rank, scoring),
          isPremium,
          scoring
        );

        userStanding.totalPoints += bonusPointsToAdd;
        userStanding.events.push({
          eventId: result.eventId,
          rank: result.rank,
          points: 0, // Top cut results don't have regular points
          pointsAwarded: bonusPointsToAdd,
          omw: 0,
          gw: 0,
          ogw: 0,
          isPremium: isPremium,
          isTopCut: true,
        });
      }
    });

    leaguePointAdjustments.forEach((adjustment: any) => {
      const playerConstruct = adjustment.playerConstructId
        ? allPlayerConstructs.find(
            (construct: any) => construct.id === adjustment.playerConstructId
          )
        : null;
      const user = adjustment.userId
        ? allUsers.find((candidate: any) => candidate.id === adjustment.userId)
        : playerConstruct?.userId
        ? allUsers.find(
            (candidate: any) => candidate.id === playerConstruct.userId
          )
        : null;
      const constructKey = adjustment.playerConstructId
        ? `construct_${adjustment.playerConstructId}`
        : null;
      const key =
        (constructKey && userStandings[constructKey] && constructKey) ||
        (playerConstruct?.userId &&
          userStandings[playerConstruct.userId] &&
          playerConstruct.userId) ||
        constructKey ||
        adjustment.userId;
      if (!key) return;

      if (!userStandings[key]) {
        userStandings[key] = {
          userId: adjustment.userId || playerConstruct?.userId || null,
          user,
          playerConstruct,
          totalPoints: 0,
          adjustmentPoints: 0,
          totalEvents: 0,
          bestRank: Infinity,
          averageRank: 0,
          totalRanks: 0,
          totalFinishPercentile: 0,
          totalWins: 0,
          totalLosses: 0,
          totalDraws: 0,
          events: [],
        };
      }
      userStandings[key].adjustmentPoints =
        (userStandings[key].adjustmentPoints || 0) + adjustment.points;
      userStandings[key].totalPoints += adjustment.points;
    });

    // Add participation bonus (1 point per event)
    Object.values(userStandings).forEach((standing: any) => {
      standing.totalPoints += calculateParticipationPoints(
        standing.totalEvents,
        scoring
      );
      standing.averageRank =
        standing.totalEvents > 0
          ? standing.totalRanks / standing.totalEvents
          : 0;
      standing.averageFinishPercentile =
        standing.totalEvents > 0
          ? standing.totalFinishPercentile / standing.totalEvents
          : 0;
    });

    const standings = Object.values(userStandings);
    if (selectedLeague?.format === "Commander") {
      return getCompetitionRanks(
        standings.sort((a: any, b: any) => b.totalPoints - a.totalPoints),
        (standing: any) => standing.totalPoints
      ).map(({ rank, ...standing }) => ({ ...standing, leagueRank: rank }));
    }
    return standings
      .sort(compareLeagueStandings)
      .map((standing: any, index) => ({
        ...standing,
        leagueRank: index + 1,
      }));
  }, [
    events,
    allStandings,
    allUsers,
    allEventPlayerResults,
    allPlayerConstructs,
    allTopCutResults,
    leaguePointAdjustments,
    scoring,
    selectedLeague,
  ]);

  // Save calculated standings to database
  React.useEffect(() => {
    if (leagueStandings.length > 0 && leagueId) {
      const standingsToSave: CalculatedStanding[] = leagueStandings
        .filter((standing: any) => standing.playerConstruct?.id) // Only save player constructs
        .map((standing: any) => ({
          playerConstructId: standing.playerConstruct.id,
          totalPoints: standing.totalPoints,
          totalEvents: standing.totalEvents,
          bestRank: standing.bestRank === Infinity ? 0 : standing.bestRank,
          averageRank: standing.averageRank,
          averageFinishPercentile: standing.averageFinishPercentile,
          currentRank: standing.leagueRank,
          totalWins: standing.totalWins,
          totalLosses: standing.totalLosses,
          totalDraws: standing.totalDraws,
        }));

      if (standingsToSave.length > 0) {
        saveLeagueStandings(leagueId, standingsToSave).catch((error) => {
          console.error("Failed to save league standings:", error);
        });
      }
    }
  }, [leagueStandings, leagueId]);

  // Filter standings based on search and minimum events
  const filteredStandings = useMemo(() => {
    let filtered = leagueStandings;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((standing: any) => {
        if (standing.user) {
          return (
            standing.user.displayName?.toLowerCase().includes(searchLower) ||
            standing.user.username?.toLowerCase().includes(searchLower) ||
            standing.user.email?.toLowerCase().includes(searchLower)
          );
        } else if (standing.playerConstruct) {
          return standing.playerConstruct.name
            ?.toLowerCase()
            .includes(searchLower);
        }
        return false;
      });
    }

    // Filter by minimum events
    const minEventsNum = parseInt(minEvents);
    if (minEventsNum > 0) {
      filtered = filtered.filter(
        (standing: any) => standing.totalEvents >= minEventsNum
      );
    }

    return filtered;
  }, [leagueStandings, searchTerm, minEvents]);

  const isLoading =
    eventsLoading ||
    standingsLoading ||
    usersLoading ||
    eventPlayerResultsLoading ||
    playerConstructsLoading ||
    topCutResultsLoading ||
    adjustmentsLoading;
  const hasError =
    eventsError ||
    standingsError ||
    usersError ||
    eventPlayerResultsError ||
    playerConstructsError ||
    topCutResultsError ||
    adjustmentsError;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          {selectedLeague && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-white/10 px-2.5 py-1 text-xs font-medium">
                {selectedLeague.name}
              </span>
              {selectedLeagueStatus && (
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    selectedLeagueStatus.status === "running"
                      ? "bg-green-500/15 text-green-300"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {selectedLeagueStatus.message}
                </span>
              )}
            </div>
          )}
          <h1 className="text-3xl font-bold tracking-tight">
            {isPastView ? "Past " : ""}
            {getLeagueGameLabel(selectedLeague?.game)} Standings
          </h1>
          <p className="text-muted-foreground">
            {selectedLeague
              ? `${selectedLeague.format} · ${selectedLeague.startDate} to ${
                  selectedLeague.endDate || "ongoing"
                }`
              : "Current rankings and statistics for all players"}
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button
            variant="outline"
            disabled={!alternateLeagueByGame[selectedGame]}
            onClick={() => {
              const destination = alternateLeagueByGame[selectedGame];
              if (destination) {
                navigate(
                  `/league/${destination.id}/standings${
                    isPastView ? "" : "/history"
                  }`
                );
              }
            }}
          >
            {isPastView ? (
              <ArrowLeft className="h-4 w-4 mr-2" />
            ) : (
              <History className="h-4 w-4 mr-2" />
            )}
            {isPastView ? "Active Leagues" : "Past Leagues"}
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Game selection */}
      <div className="grid gap-3 sm:grid-cols-3" role="tablist">
        {(Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => {
          const label = LEAGUE_GAMES[game].label;
          const Icon =
            game === "MAGIC_THE_GATHERING"
              ? Sparkles
              : game === "RIFTBOUND"
              ? Swords
              : Gamepad2;
          const gameLeague = displayedLeagueByGame[game];
          const isSelected = selectedGame === game;
          const status = gameLeague ? getLeagueStatus(gameLeague) : null;

          return (
            <Button
              key={game}
              role="tab"
              aria-selected={isSelected}
              variant={isSelected ? "default" : "outline"}
              className="h-auto justify-start px-4 py-4"
              disabled={!gameLeague}
              onClick={() =>
                gameLeague &&
                navigate(
                  `/league/${gameLeague.id}/standings${
                    isPastView ? "/history" : ""
                  }`
                )
              }
            >
              <Icon className="mr-3 h-5 w-5" />
              <span className="text-left">
                <span className="block font-semibold">{label}</span>
                <span className="block text-xs opacity-75">
                  {gameLeague
                    ? `${gameLeague.name} · ${
                        isPastView ? "Most recent past league" : status?.message
                      }`
                    : "No league available"}
                </span>
              </span>
            </Button>
          );
        })}
      </div>

      {/* League selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {isPastView ? "Past League" : "Active League"}
          </CardTitle>
          <CardDescription>
            Choose a {isPastView ? "completed" : "currently active"}{" "}
            {getLeagueGameLabel(selectedGame)} league.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LeagueSelector
            selectedLeagueId={leagueId || null}
            onLeagueChange={handleLeagueChange}
            className="max-w-xl"
            game={selectedGame}
            view={isPastView ? "past" : "active"}
          />
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={minEvents} onValueChange={setMinEvents}>
              <SelectTrigger>
                <SelectValue placeholder="Minimum Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All Players</SelectItem>
                <SelectItem value="1">1+ Events</SelectItem>
                <SelectItem value="3">3+ Events</SelectItem>
                <SelectItem value="5">5+ Events</SelectItem>
                <SelectItem value="10">10+ Events</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Standings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Current Standings
          </CardTitle>
          <CardDescription>
            Player rankings based on tournament performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading standings...</p>
            </div>
          ) : hasError ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading standings data</p>
            </div>
          ) : filteredStandings.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No Standings Available
              </h3>
              <p className="text-muted-foreground mb-4">
                {leagueStandings.length === 0
                  ? "Standings will appear here once players participate in events."
                  : "No players match your current filters."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Rank</th>
                    <th className="text-left py-3 px-4 font-medium">Player</th>
                    <th className="text-center py-3 px-4 font-medium">
                      Total Points
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      Adjustments
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      Events
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      Best Rank
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      Avg Rank
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      Finish %
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      {selectedLeague?.format === "Commander"
                        ? "Pod Win Rate"
                        : "League Win Rate"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStandings.map((standing: any) => (
                    <tr
                      key={standing.userId || standing.playerConstruct?.id}
                      className="border-b hover:bg-muted/50"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {standing.leagueRank === 1 && (
                            <Medal className="h-4 w-4 text-yellow-500" />
                          )}
                          {standing.leagueRank === 2 && (
                            <Medal className="h-4 w-4 text-gray-400" />
                          )}
                          {standing.leagueRank === 3 && (
                            <Medal className="h-4 w-4 text-amber-600" />
                          )}
                          <span className="font-medium">
                            #{standing.leagueRank}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex-shrink-0">
                            {standing.user?.profilePictureUrl ? (
                              <img
                                src={standing.user.profilePictureUrl}
                                alt={
                                  standing.user.displayName ||
                                  standing.user.username
                                }
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Replace the image with the fallback icon
                                  e.currentTarget.style.display = "none";
                                  const fallback = e.currentTarget
                                    .nextElementSibling as HTMLElement;
                                  if (fallback) {
                                    fallback.style.display = "flex";
                                  }
                                }}
                              />
                            ) : null}
                            {/* Always render the fallback, but hide it initially if image is present */}
                            <div
                              className={`w-full h-full flex items-center justify-center ${
                                standing.user?.profilePictureUrl ? "hidden" : ""
                              }`}
                            >
                              <User className="h-4 w-4 text-primary" />
                            </div>
                          </div>
                          <div>
                            <div className="font-medium">
                              {standing.user
                                ? standing.user.displayName ||
                                  standing.user.username
                                : standing.playerConstruct?.name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {standing.user
                                ? standing.user.email
                                : "Player Construct"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center font-medium">
                        {standing.totalPoints}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {standing.adjustmentPoints
                          ? `${standing.adjustmentPoints > 0 ? "+" : ""}${
                              standing.adjustmentPoints
                            }`
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {standing.totalEvents}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {standing.bestRank === Infinity
                          ? "-"
                          : `#${standing.bestRank}`}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {standing.averageRank.toFixed(1)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {standing.averageFinishPercentile.toFixed(1)}%
                      </td>
                      <td className="py-3 px-4 text-center">
                        {calculateWinRate(
                          standing.totalWins,
                          standing.totalLosses,
                          standing.totalDraws
                        ).toFixed(1)}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Standings;
